import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
    useRef,
} from 'react';

interface ContainerWidthContextValue {
    ref: (node: HTMLElement | null) => void;
    width: number;
}

const ContainerWidthContext = createContext<
    ContainerWidthContextValue | undefined
>(undefined);

export const ContainerWidthProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const containerRef = useRef<HTMLElement | null>(null);
    const [width, setWidth] = useState<number>(0);

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            // requestAnimationFrame circumvents an error, see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_errors
            // it's not ideal and we could get weird bugs in the future but for now it works
            // Really we need the container we're observing to not be a child of this provider so that it's not re-rendered repeatedly
            requestAnimationFrame(() => {
                if (entries[0]) {
                    const { width } = entries[0].contentRect;
                    setWidth(width);
                }
            });
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
                observer.disconnect();
            }
        };
    }, []);

    return (
        <ContainerWidthContext.Provider
            value={{
                ref: (node) => (containerRef.current = node),
                width,
            }}
        >
            {children}
        </ContainerWidthContext.Provider>
    );
};

export const useContainerWidth = () => {
    const context = useContext(ContainerWidthContext);

    if (!context) {
        throw new Error(
            'useContainerWidth must be used within a ContainerWidthProvider'
        );
    }

    return context;
};
